import React, { useEffect, useRef, useState } from "react";
import { FloatingLabel } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import type { SingleValue } from "react-select";

interface Option {
  key: string | number;
  name: string;
  [key: string]: unknown;
}

interface SelectObjectState {
  initialOption?: string;
  label: string;
  value: any;
  options: Option[];
  onUpdate: (selected: Option | null) => void;
  disabled?: boolean;
  cssClasses?: string;
  focused?: boolean;
  multiSelect?: boolean;
  groupHeading?: boolean;
  allowCreate?: boolean;
  onCreateOption?: (inputValue: string) => Promise<Option> | Option;
}

const SelectObjectTypeahead = ({
  initialOption = "Select from List",
  label,
  value,
  options,
  onUpdate,
  disabled = false,
  cssClasses = "",
  focused = false,
  multiSelect = false,
  groupHeading = false,
  allowCreate = false,
  onCreateOption,
}: SelectObjectState) => {
  const [internalValue, setInternalValue] = useState(value);
  const selectRef = useRef<any>(null);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (focused && selectRef.current) {
      setTimeout(() => {
        try {
          selectRef.current.focus();
        } catch (e) {
          console.warn("Select focus failed", e);
        }
      }, 0);
    }
  }, [focused]);

  const handleChange = (selected: SingleValue<{ value: any; label: string; original: Option }>) => {
    onUpdate(selected ? selected.original : null);
  };

  const handleCreate = async (inputValue: string) => {
    if (!onCreateOption) return;

    try {
      const newOption = await onCreateOption(inputValue);
      onUpdate(newOption);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      paddingTop: "1.25rem",
      borderColor: state.isFocused ? "#da7e39" : base.borderColor,
      boxShadow: state.isFocused ? " 0 0 0 0.25rem rgba(249, 98, 60, 0.5)" : base.boxShadow,
      backgroundColor: disabled ? "#e9ecef" : base.backgroundColor,
      minHeight: "58px",
      maxHeight: "350px",
      overflowY: "auto",
      textTransform: "capitalize",
    }),
    placeholder: (base: any) => ({
      ...base,
      marginTop: "0.25rem",
    }),
    option: (base: any, state: any) => {
      const isArchived = state.data.original?.is_archived === true || state.data.original?.is_archived === 1;
      const isExisting = state.data.original?.is_existing === true;
      const isAssigned = state.data.original?.hasBed === true;
      const isUnavailable = state.isDisabled || (isArchived && state?.data?.showNormal === false) || isExisting || isAssigned;
      return {
        ...base,
        color: state.isSelected ? "white" : isUnavailable ? "#a0a0a0" : "black",
        backgroundColor: state.isSelected ? "#da7e39" : state.isFocused && !state.isDisabled ? "#eeeeee" : "white",
        fontStyle: isAssigned || state.isDisabled ? "italic" : "normal",
        textTransform: "capitalize",
      };
    },
  };

  const SelectComponent = allowCreate ? CreatableSelect : Select;

  if (multiSelect) {
    const formattedOptions =
      options?.map((i) => ({
        value: i.key,
        label: `${i.name}${i.department ? " - " + i.department : ""}`,
        isDisabled: false,
      })) || [];

    const selectedValues = formattedOptions.filter((opt) => value?.includes(opt.value));

    return (
      <FloatingLabel controlId="floatingInput" label={label} className={cssClasses}>
        <SelectComponent
          ref={selectRef}
          isMulti
          value={selectedValues}
          options={formattedOptions}
          onChange={(selected: any) => onUpdate(selected?.map((s: any) => s.value))}
          isDisabled={disabled}
          classNamePrefix="react-select"
          styles={customStyles}
          placeholder={initialOption}
          {...(allowCreate && {
            onCreateOption: handleCreate,
            formatCreateLabel: (inputValue: string) => `Create "${inputValue}"`,
          })}
        />
      </FloatingLabel>
    );
  } else {
    const formattedOptions = options?.map((o) => ({
      value: o.key,
      label: `${o.name}${o.department ? " - " + o.department : ""}`,
      original: o,
    }));

    const selectedValue = internalValue ? formattedOptions.find((opt) => opt.original.key === internalValue.key) || null : null;

    return (
      <FloatingLabel controlId="floatingInput" label={label} className={cssClasses}>
        <SelectComponent
          ref={selectRef}
          value={groupHeading ? internalValue : selectedValue}
          options={groupHeading ? options : formattedOptions}
          onChange={handleChange}
          isDisabled={disabled}
          placeholder={initialOption}
          styles={customStyles}
          {...(allowCreate && {
            onCreateOption: handleCreate,
            formatCreateLabel: (inputValue: string) => `Create "${inputValue}"`,
          })}
        />
      </FloatingLabel>
    );
  }
};

export default SelectObjectTypeahead;
